/****************************************************************************************************
 * COMPONENT: SECTION
 * --------------------------------------------------------------------------------------------------
 * This component is used to render a section.
 * *************************************************************************************************/

import React from 'react';
import { Container, Box } from '@mui/material';
import { SanitySection } from 'src/types/sanity';
import { BasicPortableText, SectionBlocks, LayoutNodes, BackgroundImage } from 'src/components';
import { useStoreV2 } from 'src/store/StoreV2';
import { Edit, TopNav } from 'src/components';
import { useDevMode } from 'src/hooks';

// * SECTION TYPES
import Carousel from './carousel/Carousel';
import { Layout } from 'antd';
import _ from 'lodash';

// Need to get all of the section data
interface SectionProps {
  sections: SanitySection[] | null;
  carouselItem?: boolean
}

const Section = ({ sections = null, carouselItem }: SectionProps) => {
  // Bail if there are no sections
  if (!sections) return null;


  // Determine if you are in dev mode
  const isDev = useDevMode();

  {
    /********************************************************************************
     * * Store V2
     * ------------------------------------------------------------------------------
     * Store V2 contains shared state and action data that is used across the entire
     * application. Attributes can be destructured from the store and used in the
     * component. The component will only subscribe to the attributes that you
     * destructure and subscribe to. This is a performance optimization.
     * ******************************************************************************/
  }
  // Subscribe to layout data in the store
  const layout = useStoreV2().layout?.sectionLayout ?? null;

  /********************************************************************************
   * * SECTION TYPE WRAPPER
   * ------------------------------------------------------------------------------
   * The purpose of the following code is to render a wrapper around the section
   * if necessary based on the section type. There are certain section types that
   * require a wrapper around the section. This is because the section type may
   * require a specific layout or treatment. For example, a carousel section may
   * require a specific layout or treatment.
   * ******************************************************************************/
  interface SectionTypeWrapperProps {
    type: string;
    children: React.ReactNode;
    value: any;
  }
  const SectionTypeWrapper = ({ type, children, value }: SectionTypeWrapperProps) => {
    switch (type) {
      case 'carousel':
        return <Carousel value={value}>{children}</Carousel>;
      default:
        return <>{children}</>;
    }
  };

  const LayoutWrapper = ({ 
    type,     
    children
   }: any) => {
    switch (type) {
      case 'carousel':
        return <Box>{children}</Box>;
      default:
        if (carouselItem) return (<Box>{children}</Box>);
        return (          
          <>
            <Box
              className={`elo-section-blocks-root`}
              border={isDev ? '1px solid tomato' : undefined}
              // Apply margin values from the layout data in the store
              m={layout?.margin?.m ?? undefined}
              mx={layout?.margin?.mx ?? undefined}
              my={layout?.margin?.my ?? undefined}
              mt={layout?.margin?.mt ?? undefined}
              mr={layout?.margin?.mr ?? undefined}
              mb={layout?.margin?.mb ?? undefined}
              ml={layout?.margin?.ml ?? undefined}
              // Apply padding values from the layout data in the store
              p={layout?.padding?.p ?? undefined}
              px={layout?.padding?.px ?? undefined}
              py={layout?.padding?.py ?? undefined}
              pt={layout?.padding?.pt ?? undefined}
              pr={layout?.padding?.pr ?? undefined}
              pb={layout?.padding?.pb ?? undefined}
              pl={layout?.padding?.pl ?? undefined}
            >
              {children}
            </Box>
          </>
        );
    }
  };

  return (
    <>
      {sections.map((section, index) => {

        {/********************************************************************************
         * * RETURN SPECIAL SECTION TYPES         
         * ------------------------------------------------------------------------------
         * If the section is not the basic section then return the special section type.
         * ******************************************************************************/}
        if (section?.__typename === 'SanityTopNav') {
          return <TopNav key={index} value={section}/>;
        }

        const getColor = (type: 'backgroundColor' | 'overlayColor') => {
          let color;
          if (type === 'overlayColor') color = section?.overlayColor?._rawRgb ?? null;
          if (type === 'backgroundColor') color = section?.backgroundColor?._rawRgb ?? null;
          if (color) {
            return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
          }
        };                  

        
        return (
          <>
            <SectionTypeWrapper
              key={index}
              value={section?.carouselSections ?? null}
              type={section?.sectionType}
            >
              {/********************************************************************************
               * * ROOT SECTION CONTAINER
               * - elo-section-root
               * ------------------------------------------------------------------------------
               * This initial section contain is always 100vw of the viewport. Inside this
               * container is where we can place background colors, images, etc.
               * ******************************************************************************/}
              <Box
                position={'relative'}
                key={index}
                border={isDev ? '1px solid cyan' : undefined}
                className="elo-section-root"
              >
                {/********************************************************************************
                 * * BACKGROUND COLOR
                 * ------------------------------------------------------------------------------
                 * ******************************************************************************/}
                <Box
                  zIndex={-3}
                  position={'absolute'}
                  sx={{
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: getColor('backgroundColor'),
                  }}
                />

                {/********************************************************************************
                 * * BACKGROUND IMAGE
                 * ------------------------------------------------------------------------------
                 * ******************************************************************************/}
                <BackgroundImage imageData={section?.backgroundImage?._rawImage ?? null} />

                {/********************************************************************************
                 * * OVERLAY COLOR
                 * ------------------------------------------------------------------------------
                 * ******************************************************************************/}
                <Box
                  zIndex={-1}
                  position={'absolute'}
                  sx={{
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: getColor('overlayColor'),
                  }}
                />

                <Edit documentType="section" documentId={section?._id} />

                {/********************************************************************************
                 * * CONTENT SECTION CONTAINER
                 * - elo-section-grid-root
                 * ------------------------------------------------------------------------------
                 * The contentent section container centers itself within the root section and
                 * provides a container that enforces the fixed max width of the content that
                 * uses the site themes default breakpoints. If you want to update the breakpoints
                 * do not manuall add those here. Instead, update the theme breakpoints in the
                 * theme definition which may be a file or more likely the brand or theme
                 * definition in the CMS.
                 *
                 * NOTE: The container is set to disable gutters. This is because we want fine
                 * grained control over the gutters. We will use an child Box element to apply
                 * gutters using margin and padding values from the CMS. In the CMS you will
                 * find these values in the 'Brand' that is defined for the page.
                 * ******************************************************************************/}
                <Container
                  fixed
                  disableGutters
                  className="elo-section-grid-root"
                  sx={{
                    border: isDev ? '1px dashed magenta' : undefined,
                  }}
                >
                  {/********************************************************************************
                   * * SECTION BLOCKS
                   * - elo-section-blocks-root
                   * ------------------------------------------------------------------------------
                   * The section blocks have a root wrapper. This is so that we can add style
                   * treatments to the section blocks root. The container also, applies a default
                   * gutter on the left and right to ensure that the blocks are not touching the
                   * edge of the viewport. Adding a root level wrapper to the blocks allows us to
                   * use this as the starting point for any layout treatments.
                   *
                   * NOTE: This Box wraps the PortableText component and uses the Box components
                   * theme aware margin and padding values. This provided fine grained control
                   * of the layout and spacing of the content. It replaces the need for the
                   * gutters normally provided by the Container component. Gutters have been
                   * disabled on the Container component.
                   * ******************************************************************************/}
                  <LayoutNodes layoutNodes={section?.layoutNodes}>
                    {/* <Box
                      className={`elo-section-blocks-root`}
                      border={isDev ? '1px solid tomato' : undefined}
                      // Apply margin values from the layout data in the store
                      m={layout?.margin?.m ?? undefined}
                      mx={layout?.margin?.mx ?? undefined}
                      my={layout?.margin?.my ?? undefined}
                      mt={layout?.margin?.mt ?? undefined}
                      mr={layout?.margin?.mr ?? undefined}
                      mb={layout?.margin?.mb ?? undefined}
                      ml={layout?.margin?.ml ?? undefined}
                      // Apply padding values from the layout data in the store
                      p={layout?.padding?.p ?? undefined}
                      px={layout?.padding?.px ?? undefined}
                      py={layout?.padding?.py ?? undefined}
                      pt={layout?.padding?.pt ?? undefined}
                      pr={layout?.padding?.pr ?? undefined}
                      pb={layout?.padding?.pb ?? undefined}
                      pl={layout?.padding?.pl ?? undefined}
                    > */}
                    <LayoutWrapper type={section?.sectionType}>
                      <BasicPortableText content={section?._rawContent} />
                      <SectionBlocks blocks={section?.sectionBlocks ?? null} />
                    </LayoutWrapper>
                    {/* </Box> */}
                  </LayoutNodes>
                </Container>
              </Box>
            </SectionTypeWrapper>
          </>
        );
      })}
    </>
  );
};

export default Section;
